import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/blog/woocommerce-zu-shopify-migration";
const TITLE = "WooCommerce zu Shopify migrieren: Schritt-für-Schritt ohne Rankings-Verlust";
const DESCRIPTION =
  "WooCommerce zu Shopify migrieren: Datenübernahme, URL-Weiterleitungen, SEO-Sicherung und Livegang in klaren Schritten. Mit Kostenrahmen und typischen Fehlerquellen.";
const UPDATED = "2026-09-10";

const ANSWER =
  "Eine WooCommerce-zu-Shopify-Migration läuft in fünf Schritten: Bestandsaufnahme aller URLs und Sonderfälle, Datenübernahme per Shopify-Importer, Aufbau des Themes, vollständiger 301-Weiterleitungsplan und gestaffelter Livegang. Rankings bleiben erhalten, wenn jede alte URL ein Ziel erhält und die Seitenstruktur weitgehend erhalten bleibt. Rechnen Sie für kleinere Shops drei bis sechs Wochen.";

const faqs = [
  {
    q: "Wie migriere ich von WooCommerce zu Shopify?",
    a: "Im Importer von Shopify lassen sich Produkte, Kunden und Bestellungen aus einer WooCommerce-XML- oder CSV-Datei übertragen. Wichtiger als der Import ist der 301-Weiterleitungsplan: Jede alte URL braucht ein neues Ziel, sonst verlieren Sie Rankings und eingehende Links.",
  },
  {
    q: "Geht der Google-Verkehr bei der Migration verloren?",
    a: "Nicht, wenn jede alte URL per 301 weitergeleitet wird und die wesentliche Seitenstruktur erhalten bleibt. Übliche Sichtbarkeitsschwankungen halten ein bis acht Wochen an, danach liegt der Traffic meist wieder auf oder über dem Ausgangsniveau.",
  },
  {
    q: "Welche Daten lassen sich von WooCommerce übernehmen?",
    a: "Produkte mit Varianten und Bildern, Kundenkonten, Bestellhistorie und Rabattcodes. Kundenpasswörter werden nicht übernommen, Kunden setzen sie beim ersten Login im neuen Shop neu.",
  },
  {
    q: "Wie lange dauert die Migration von WooCommerce zu Shopify?",
    a: "Bis etwa 1.000 Produkte drei bis sechs Wochen. Der Aufwand hängt weniger an der Produktzahl als an Sonderfällen wie Variantenlogik, Abo-Produkten, ERP- oder PIM-Schnittstellen und individuellen Checkout-Anpassungen.",
  },
  {
    q: "Was kostet eine WooCommerce-zu-Shopify-Migration?",
    a: "Kleinere Shops liegen bei 4.000 bis 8.000 € netto, mittlere Sortimente mit Schnittstellen bei 8.000 bis 20.000 €. Eine reine Datenübernahme ohne neues Theme ist deutlich günstiger, ein neues Theme und Conversion-Optimierung heben den Rahmen.",
  },
];

export const Route = createFileRoute("/blog/woocommerce-zu-shopify-migration")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
      breadcrumb: { name: "Blog", path: "/blog/woocommerce-zu-shopify-migration" },
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <ArticlePage
      kicker="Blog · Migration"
      title="WooCommerce zu Shopify migrieren"
      intro="Wer von WooCommerce zu Shopify umzieht, tauscht nicht nur eine Plattform, sondern ein komplettes System aus Plugin- und Hosting-Abhängigkeiten. Mit der richtigen Reihenfolge bleibt der Google-Verkehr erhalten, und der Umzug wird zur Conversion-Chance statt zum Risiko."
      source="blog-woocommerce-zu-shopify-migration"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/ratgeber", label: "Ratgeber" }}
      sections={[
        {
          h: "Warum von WooCommerce zu Shopify wechseln",
          p: [
            "WooCommerce ist flexibel, verschiebt die Verantwortung für Hosting, Updates, Sicherheit und Performance aber vollständig auf den Betreiber. Jedes Plugin bringt Abhängigkeiten, und jede WordPress- und PHP-Aktualisierung kann eine Kaufkette blockieren.",
            "Shopify übernimmt Hosting, SSL, Performance und Checkout-Sicherheit als Plattform. Dadurch entfällt ein Großteil des Betriebsaufwands, und das Team kann sich auf Sortiment, Inhalte und Conversion-Rate konzentrieren.",
          ],
        },
        {
          h: "Schritt 1: Bestandsaufnahme",
          p: [
            "Vor dem ersten Handgriff steht der Export. Ziehen Sie alle URLs aus WooCommerce und aus der Google Search Console, sortiert nach Sichtbarkeit und Umsatz. Diese Liste entscheidet später über den Weiterleitungsplan.",
          ],
          list: [
            "Vollständigen URL-Export aus WooCommerce und der Search Console",
            "Sortiment prüfen: Varianten, Abos, staffelfähige Preise, B2B-Kontingente",
            "Schnittstellen inventarisieren: ERP, PIM, Warenwirtschaft, Marketing-Tools",
            "Seiten mit Rankings und Backlinks separat markieren",
          ],
        },
        {
          h: "Schritt 2: Datenübernahme",
          p: [
            "Der Shopify-Importer überträgt Produkte, Kunden und Bestellungen aus einer WooCommerce-Datei. Achten Sie darauf, dass Varianten, Bilder, Steuerschlüssel und Gewicht korrekt ankommen, denn nach dem Import nachzubessern ist aufwendiger als ein sauberer Erstabgleich.",
          ],
          list: [
            "Produkte inklusive Varianten, Bildern und Metadaten prüfen",
            "Steuerschlüssel und Versandklassen zuordnen",
            "Bestellhistorie stichprobenartig kontrollieren",
            "Kundenkonten anlegen, Passwörter werden neu vergeben",
          ],
        },
        {
          h: "Schritt 3: Theme und Conversion-Struktur",
          p: [
            "Den alten WooCommerce-Shop eins zu eins nachzubauen, bringt dieselben Zahlen bei neuem Wartungsmodell. Sinnvoller ist es, Produktseite, Warenkorb und Checkout bewusst neu zu strukturieren und die zwei bis drei größten Umsatzlecks direkt im neuen Theme zu schließen.",
          ],
        },
        {
          h: "Schritt 4: Der 301-Weiterleitungsplan",
          p: [
            "Hier scheitern die meisten Migrationen. Jede alte WooCommerce-URL braucht ein 301-Ziel, ohne Ausnahme. Sammelweiterleitungen auf die Startseite werteten eingehende Links und Rankings ab.",
          ],
          list: [
            "Jede alte URL einem neuen Ziel zuordnen",
            "Produkte, Kategorien, Blogbeiträge und Tag-Seiten einzeln weiterleiten",
            "Filter- und Paginierungsseiten mit Rankings nicht vergessen",
            "Weiterleitungen vor dem Livegang testen, nicht danach",
            "Nach dem Umzug wöchentlich Crawling-Fehler in der Search Console prüfen",
          ],
        },
        {
          h: "Schritt 5: Livegang und erste Wochen",
          p: [
            "Der Umzug findet an einem umsatzschwachen Wochentag statt. Danach werden Kaufabschlussrate, Add-to-Cart-Rate und Umsatz pro Session täglich mit den Vorwochen verglichen, so fallen Fehler in Stunden und nicht erst im Monatsbericht auf.",
            "In den ersten vier Wochen sind Suchanfragen und Conversions volatil. Halten Sie alte Werbekampagnen kurzfristig zurück und beobachten Sie die Indexierung der neuen URLs.",
          ],
        },
        {
          h: "Kostenrahmen für die Migration",
          p: [
            "Kleinere Shops bis rund 1.000 Produkte liegen bei 4.000 bis 8.000 € netto, mittlere Sortimente mit Schnittstellen bei 8.000 bis 20.000 €. Eine reine Datenübernahme ohne neues Theme ist deutlich günstiger, Theme-Aufbau und Conversion-Optimierung heben den Rahmen.",
            "Theme-Anpassungen und Entwicklung berechne ich mit 110 € netto pro Stunde, die Conversion-Optimierung ab 3.990 € netto. Für die Migration selbst gibt es nach Sichtung ein festes Angebot.",
          ],
        },
        {
          h: "Typische Fehlerquellen",
          p: [
            "Die häufigsten Probleme sind unvollständige Weiterleitungen, fehlende Steuer- und Versandklassen, nicht übernommene Bestellhistorie und Plugins, deren Funktion im neuen Shop fehlt. Eine saubere Bestandsaufnahme verhindert fast alle davon.",
          ],
          list: [
            "Sammel-301 auf die Startseite statt einzelner Weiterleitungen",
            "Fehlende Varianten oder Bilder nach dem Import",
            "Steuerschlüssel und Versandklassen nicht zugeordnet",
            "Plugin-Funktionen, die im neuen Shop nicht ersetzt wurden",
            "Kein Monitoring der Kaufabschlussrate nach dem Livegang",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-vs-woocommerce", label: "Shopify vs. WooCommerce" },
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration im Überblick" },
        { to: "/cases", label: "Kundenprojekte ansehen" },
        { to: "/kontakt", label: "Migration anfragen" },
      ]}
    />
  );
}
